"use client";

import {
	type CSSProperties,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import {
	createVisitorActivityStore,
	DEFAULT_VISITOR_ACTIVITY_TIMING_RANGES,
	getSeededRangeValue,
	getVisitorActivityCurrencyFromPricingCountry,
	INITIAL_VISITOR_ACTIVITY_BATCH_SIZE,
	loadVisitorActivityStore,
	persistVisitorActivityStore,
	REFILL_VISITOR_ACTIVITY_BATCH_SIZE,
	reserveVisitorActivityBatch,
	type VisitorActivityCurrency,
	type VisitorActivityPurchase,
	type VisitorActivityStore,
	type VisitorActivityTimingRange,
} from "@/lib/visitor-activity-popups";

interface VisitorActivityPopupsProps {
	initialDelayMs?: number;
	intervalMs?: number;
	visibleDurationMs?: number;
	initialDelayRangeMs?: VisitorActivityTimingRange;
	intervalRangeMs?: VisitorActivityTimingRange;
	visibleDurationRangeMs?: VisitorActivityTimingRange;
	initialBatchSize?: number;
	refillBatchSize?: number;
	seed?: number;
	disabled?: boolean;
	currency?: VisitorActivityCurrency;
}

interface PricingCountryMetaResponse {
	meta?: {
		country?: string;
	};
}

function resolveTimingRange(
	fixedDelayMs: number | undefined,
	rangeMs: VisitorActivityTimingRange | undefined,
	defaultRangeMs: VisitorActivityTimingRange,
): VisitorActivityTimingRange {
	if (fixedDelayMs !== undefined) return [fixedDelayMs, fixedDelayMs];
	return rangeMs ?? defaultRangeMs;
}

const jakartaFontStyle: CSSProperties = {
	fontFamily:
		"var(--font-popup-jakarta), var(--font-jost), ui-sans-serif, system-ui, sans-serif",
};

const interFontStyle: CSSProperties = {
	fontFamily:
		"var(--font-popup-inter), var(--font-raleway), ui-sans-serif, system-ui, sans-serif",
};

const POPUP_ENTER_DELAY_MS = 20;
const POPUP_EXIT_DURATION_MS = 180;
const VISITOR_ACTIVITY_COUNTRY_META_ENDPOINT = "/api/pricing?meta=country";
const VISITOR_ACTIVITY_COUNTRY_FETCH_TIMEOUT_MS = 3500;

type VisitorActivityPopupPhase = "entering" | "visible" | "exiting";

async function fetchTrustedVisitorCurrency(
	signal: AbortSignal,
): Promise<VisitorActivityCurrency | null> {
	if (typeof fetch !== "function") return null;

	try {
		const response = await fetch(VISITOR_ACTIVITY_COUNTRY_META_ENDPOINT, {
			cache: "no-store",
			headers: { accept: "application/json" },
			signal,
		});
		if (!response.ok) return null;

		const payload = (await response.json()) as PricingCountryMetaResponse;
		return getVisitorActivityCurrencyFromPricingCountry(payload.meta?.country);
	} catch {
		return null;
	}
}

function splitPlanLine(planName: string) {
	const separator = " for ";
	const separatorIndex = planName.lastIndexOf(separator);

	if (separatorIndex === -1) {
		return { name: planName, price: "" };
	}

	return {
		name: planName.slice(0, separatorIndex),
		price: planName.slice(separatorIndex + separator.length),
	};
}

export function VisitorActivityPopups({
	initialDelayMs,
	intervalMs,
	visibleDurationMs,
	initialDelayRangeMs,
	intervalRangeMs,
	visibleDurationRangeMs,
	initialBatchSize = INITIAL_VISITOR_ACTIVITY_BATCH_SIZE,
	refillBatchSize = REFILL_VISITOR_ACTIVITY_BATCH_SIZE,
	seed,
	disabled = false,
	currency: currencyOverride,
}: VisitorActivityPopupsProps) {
	const [activePurchase, setActivePurchase] =
		useState<VisitorActivityPurchase | null>(null);
	const [popupPhase, setPopupPhase] =
		useState<VisitorActivityPopupPhase>("entering");
	const [hasFirstTimeOfferBanner, setHasFirstTimeOfferBanner] = useState(false);
	const queueRef = useRef<VisitorActivityPurchase[]>([]);
	const storeRef = useRef<VisitorActivityStore | null>(null);
	const activePopupIdRef = useRef(0);
	const popupPhaseRef = useRef<VisitorActivityPopupPhase>("entering");
	const shownCountRef = useRef(0);

	const setPopupPhaseState = useCallback((phase: VisitorActivityPopupPhase) => {
		popupPhaseRef.current = phase;
		setPopupPhase(phase);
	}, []);

	useEffect(() => {
		const updateOfferBannerOffset = () => {
			const nextValue = Boolean(
				document.querySelector("[data-highrays-first-time-offer-banner]"),
			);

			setHasFirstTimeOfferBanner((currentValue) =>
				currentValue === nextValue ? currentValue : nextValue,
			);
		};

		updateOfferBannerOffset();

		const observer = new MutationObserver(updateOfferBannerOffset);
		observer.observe(document.body, { childList: true, subtree: true });

		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		if (disabled) return;

		let isDisposed = false;
		let currencyController: AbortController | null = null;
		const timers = new Set<number>();

		const schedule = (callback: () => void, delayMs: number) => {
			const timer = window.setTimeout(() => {
				timers.delete(timer);
				callback();
			}, delayMs);
			timers.add(timer);
		};

		const persistStore = () => {
			if (!storeRef.current) return;
			try {
				persistVisitorActivityStore(window.localStorage, storeRef.current);
			} catch {}
		};

		const reserveBatch = (batchSize: number) => {
			if (!storeRef.current) return [];

			const reserved = reserveVisitorActivityBatch(storeRef.current, batchSize);
			storeRef.current = reserved.store;
			persistStore();
			return reserved.batch;
		};

		const initialRange = resolveTimingRange(
			initialDelayMs,
			initialDelayRangeMs,
			DEFAULT_VISITOR_ACTIVITY_TIMING_RANGES.initialDelayMs,
		);
		const intervalRange = resolveTimingRange(
			intervalMs,
			intervalRangeMs,
			DEFAULT_VISITOR_ACTIVITY_TIMING_RANGES.intervalMs,
		);
		const visibleDurationRange = resolveTimingRange(
			visibleDurationMs,
			visibleDurationRangeMs,
			DEFAULT_VISITOR_ACTIVITY_TIMING_RANGES.visibleDurationMs,
		);

		const getDelay = (salt: string, range: VisitorActivityTimingRange) =>
			getSeededRangeValue(
				storeRef.current?.seed ?? 1,
				shownCountRef.current,
				salt,
				range,
			);

		const startWithCurrency = (currency: VisitorActivityCurrency) => {
			if (isDisposed) return;

			try {
				storeRef.current = loadVisitorActivityStore(window.localStorage, seed, {
					currency,
				});
			} catch {
				storeRef.current = createVisitorActivityStore({ seed, currency });
			}

			queueRef.current = reserveBatch(initialBatchSize);

			const showNextPurchase = () => {
				if (isDisposed) return;

				if (queueRef.current.length === 0) {
					queueRef.current = reserveBatch(refillBatchSize);
				}

				const nextPurchase = queueRef.current.shift();
				if (!nextPurchase) {
					setActivePurchase(null);
					return;
				}

				shownCountRef.current += 1;
				activePopupIdRef.current += 1;
				const popupId = activePopupIdRef.current;

				setActivePurchase(nextPurchase);
				setPopupPhaseState("entering");
				schedule(() => {
					if (
						activePopupIdRef.current === popupId &&
						popupPhaseRef.current === "entering"
					) {
						setPopupPhaseState("visible");
					}
				}, POPUP_ENTER_DELAY_MS);
				schedule(
					() => {
						if (activePopupIdRef.current === popupId) {
							setPopupPhaseState("exiting");
							schedule(() => {
								if (activePopupIdRef.current === popupId) {
									setActivePurchase(null);
								}
							}, POPUP_EXIT_DURATION_MS);
						}
					},
					getDelay("visible", visibleDurationRange),
				);
				schedule(showNextPurchase, getDelay("interval", intervalRange));
			};

			schedule(showNextPurchase, getDelay("initial", initialRange));
		};

		if (currencyOverride) {
			startWithCurrency(currencyOverride);
		} else {
			currencyController = new AbortController();
			const timeout = window.setTimeout(() => {
				timers.delete(timeout);
				currencyController?.abort();
			}, VISITOR_ACTIVITY_COUNTRY_FETCH_TIMEOUT_MS);
			timers.add(timeout);

			fetchTrustedVisitorCurrency(currencyController.signal).then(
				(trustedCurrency) => {
					if (isDisposed) return;
					window.clearTimeout(timeout);
					timers.delete(timeout);
					startWithCurrency(trustedCurrency ?? "USD");
				},
			);
		}

		return () => {
			isDisposed = true;
			currencyController?.abort();
			for (const timer of timers) {
				window.clearTimeout(timer);
			}
			timers.clear();
		};
	}, [
		disabled,
		initialDelayMs,
		initialDelayRangeMs,
		intervalMs,
		intervalRangeMs,
		visibleDurationMs,
		visibleDurationRangeMs,
		initialBatchSize,
		refillBatchSize,
		seed,
		currencyOverride,
		setPopupPhaseState,
	]);

	if (!activePurchase) return null;

	const planLine = splitPlanLine(activePurchase.planName);
	const bottomOffsetClass = hasFirstTimeOfferBanner
		? "bottom-[calc(env(safe-area-inset-bottom)+5.9rem)] sm:bottom-[calc(env(safe-area-inset-bottom)+6.35rem)]"
		: "bottom-[calc(env(safe-area-inset-bottom)+0.875rem)] sm:bottom-5";
	const popupMotionClass =
		popupPhase === "visible"
			? "translate-y-0 scale-100 opacity-100 duration-[220ms]"
			: "translate-y-2 scale-[0.985] opacity-0 duration-[180ms]";

	return (
		<div
			data-highrays-visitor-activity-popups=""
			className={`pointer-events-none fixed left-3 z-[70] hidden w-[calc(100vw-1.5rem)] max-w-[17.5rem] transition-[bottom] duration-300 sm:left-5 sm:block ${bottomOffsetClass}`}
		>
			<output
				aria-live="polite"
				data-popup-phase={popupPhase}
				className={`relative isolate grid min-h-[3.875rem] overflow-hidden rounded-[10px] bg-white px-[9px] pt-[7px] pb-0 text-zinc-950 shadow-[0_0_18px_rgba(239,68,68,0.16),0_16px_42px_rgba(9,9,11,0.14),0_6px_16px_rgba(9,9,11,0.08)] transition-[opacity,translate,scale] ease-out will-change-[opacity,translate,scale] motion-reduce:translate-y-0 motion-reduce:scale-100 motion-reduce:transition-opacity ${popupMotionClass}`}
			>
				<div
					className="absolute -inset-8 -z-10 animate-[visitor-activity-shader_5.4s_linear_infinite] bg-[radial-gradient(circle_at_18%_20%,rgba(255,0,0,0.16),transparent_34%),linear-gradient(118deg,rgba(255,0,0,0.1),transparent_42%,rgba(239,68,68,0.1))] bg-[length:170%_170%] opacity-90"
					aria-hidden="true"
				/>
				<div
					className="absolute inset-px -z-10 rounded-[9px] bg-white/75 backdrop-blur-md"
					aria-hidden="true"
				/>
				<p
					className="-mx-[9px] -mt-[7px] flex min-w-0 items-baseline gap-1 overflow-hidden whitespace-nowrap rounded-t-[10px] bg-gradient-to-r from-red-600 to-red-500 px-[9px] pt-[7px] pb-[5px] text-[12.2px] font-semibold leading-none text-white"
					style={jakartaFontStyle}
				>
					<span
						className="min-w-0 overflow-hidden text-ellipsis"
						data-highrays-visitor-channel-name=""
						data-testid="visitor-activity-channel-name"
					>
						{activePurchase.channelName}
					</span>
					<span
						className="shrink-0 text-[10.2px] font-medium text-white/75"
						style={interFontStyle}
					>
						bought
					</span>
				</p>
				<p className="mt-[5px] flex min-w-0 items-center gap-1 overflow-hidden whitespace-nowrap pb-[7px] text-zinc-950">
					<span
						className="min-w-0 overflow-hidden text-ellipsis text-[12.6px] font-bold leading-[1.08]"
						style={jakartaFontStyle}
						data-highrays-visitor-plan-name=""
						data-testid="visitor-activity-plan-name"
					>
						{planLine.name}
					</span>
					{planLine.price ? (
						<>
							<span
								className="shrink-0 text-[10.2px] font-medium leading-none text-zinc-500"
								style={interFontStyle}
							>
								for
							</span>
							<strong
								className="inline-flex h-[18px] shrink-0 items-center rounded-[5px] bg-red-600 px-1.5 py-0 text-[11.8px] font-bold leading-none text-white"
								style={interFontStyle}
								data-highrays-visitor-plan-price=""
								data-testid="visitor-activity-plan-price"
							>
								{planLine.price}
							</strong>
						</>
					) : null}
				</p>
			</output>
		</div>
	);
}
